import React, { useCallback, useState } from "react";
import type { Contract } from "@meupedaco/shared";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type Option = { id: number; label: string };

export const ContractRenewalsPage: React.FC = () => {
  const { client } = useAuth();
  const [entityOption, setEntityOption] = useState<Option | null>(null);
  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
  } = usePagedQuery<Contract>({
    endpoint: "/modules/contracts/renewals",
    filters: {
      entityId: entityOption?.id,
    },
  });

  const fetchEntities = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/entities?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as { data: Array<{ id: number; name: string }> };
      return payload.data.map((item) => ({ id: item.id, label: item.name }));
    },
    [client]
  );

  const paginationText = React.useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Renovacoes de contratos</h1>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <AutocompleteInput
                label="Entidade"
                placeholder="Buscar entidade"
                value={entityOption?.id ?? null}
                selectedLabel={entityOption?.label}
                fetchOptions={fetchEntities}
                onSelect={setEntityOption}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Busca</label>
              <input
                className="form-control"
                placeholder="Buscar por titulo"
                value={q}
                onChange={(event) => setQ(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <PageState loading={loading} error={error}>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Titulo</th>
                    <th>Inicio</th>
                    <th>Fim</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item.id}>
                      <td>{item.title}</td>
                      <td>{formatDateBR(item.startDate)}</td>
                      <td>{formatDateBR(item.endDate)}</td>
                      <td>{item.paymentType}</td>
                      <td>{formatMoneyBR(item.amount)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        Nenhum contrato na janela de renovacao.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PageState>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">{paginationText}</small>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page * take >= total}
                onClick={() => setPage(page + 1)}
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
